<script setup lang="ts">
import dayjs from "dayjs";
// import { ref, computed } from "vue";
// import { useStore } from "vuex";
import { useToastStore } from "@/store/toast";
import { useDefaultStore } from "@/store";
import { useTempStoreOptions } from "@/store/tempOptions";
import { parseErrors } from "@/assets/js/parseErrors";
import api from "@/api";

const defaultStore = useDefaultStore();
const systemPermissions = defaultStore.initialStatus;
const userStoreOpt = useTempStoreOptions();
const isLoadingSubmitShift = ref(false);
const isLoadingShiftData = ref(false);

const toast = useToastStore();

// const store = useStore();
type TShift = {
	id: number | any;
	// NRML, ONCL, SLEP
	type: any; //"NRML" | "ONCL" | "SLEP";
	location: any;
	ward: any;
	grade: any;
	startTime: string;
	endTime: string;
	startDate: string;
	endDate: string;
	status?: string;
	breakMinutes?: number | null;
	additionalTime: {
		isUsed: boolean;
		startTime?: string;
		endTime?: string;
		startDate: string | any;
		endDate: string | any;
	};
};

const emit = defineEmits(["close-modal", "confirm"]);
const props = defineProps<{
	shift_id: any;
}>();

const shiftType = ref({
	regular: "NRML",
	standby: "ONCL",
	sleepover: "SLPO",
	sleepoverAwaken: "AWKE", // AWKE
});

const tempHasStandby = computed(() => {
	return (
		// this.$store.getters.agencyHasStandby && this.$store.getters.tempHasStandby
		// agencyHasStandby is same as can_use_on_call
		systemPermissions.can_use_on_call && userStoreOpt.tempHasStandby
	);
});
const tempHasSleepover = computed(() => {
	return (
		// this.$store.getters.agencyHasSleepover &&
		// this.$store.getters.tempHasSleepover
		// agencyHasSleepover is same as can_use_sleepover
		systemPermissions.can_use_sleepover && userStoreOpt.tempHasSleepover
	);
});
const isStandbyShift = computed(() => {
	return shift.value?.type?.code === shiftType.value.standby;
});
const isSleepoverShift = computed(() => {
	return shift.value?.type?.code === shiftType.value.sleepover;
});
const canEditBreakMinutes = computed(() => {
	if (!initialShiftType.value) return false;

	return ![shiftType.value.standby, shiftType.value.sleepover].includes(
		initialShiftType.value,
	);
});

const isShiftCreatedViaSelfSubmission = computed(() => {
	return shift.value?.status === "Temp assigned";
});

const arrShiftTypesShow = computed(() => {
	const arr = [
		{
			id: "regular",
			name: "Regular shift",
			code: "NRML",
		},
	];
	if (tempHasStandby && locationHasStandby) {
		arr.push({
			id: "standby",
			name: "Standby shift",
			code: "ONCL",
		});
	}
	if (tempHasSleepover && locationHasSleepover.value) {
		arr.push({
			id: "sleepover",
			name: "Sleepover shift",
			code: "SLPO",
		});
	}
	return arr;
});

// Reactive state
const refreshTime = ref(true);
const weekEarlier = ref(new Date(new Date().setDate(new Date().getDate() - 8)));
const canAddSpecialNote = ref(false);
const clientNote = ref("");
const initialStartDate = ref("");
const initialEndDate = ref("");
const initialShiftType = ref("");

// const selectedShift = ref<any>({
// 	type: "",
// 	locId: null,
// 	wardId: null,
// 	gradeId: null,
// 	startTime: "00:00",
// 	endTime: "00:00",
// 	startDate: null,
// 	endDate: null,
// });

// const calendarStart = ref<any>(null);
// const calendarEnd = ref<any>(null);
const locations = ref<any[]>([]);
const wards = ref<any[]>([]);
const grades = ref<any[]>([]);

const shift = ref<TShift>({
	id: null,
	// NRML, ONCL, SLEP
	type: "NRML",
	location: null,
	ward: null,
	grade: null,
	startTime: "00:00",
	endTime: "00:00",
	startDate: null,
	endDate: null,
	status: "",
	breakMinutes: null,
	additionalTime: {
		isUsed: false,
		// startTime: "00:00",
		// endTime: "00:00",
		startDate: dayjs().startOf("day"),
		endDate: dayjs().startOf("day"),
	},
});

const error = ref({
	location: false,
	ward: false,
	grade: false,
	startDate: false,
	endDate: false,
	startTime: false,
	endTime: false,
});

// const activeStatus = ref<number | null>(null);

// Computed properties

const locationHasStandby = computed(() => {
	if (shift.value?.ward?.hasOnCall) return true;
	return false;
});

const locationHasSleepover = computed(() => {
	if (shift.value?.ward?.hasSleepover) return true;
	return false;
});

const additionalTimeTitle = computed(() => {
	switch (shift.value.type?.code) {
		case shiftType.value.sleepover: {
			return "Sleeping Time";
			break;
		}
		case shiftType.value.standby: {
			return "On Call";
			break;
		}
		default: {
			return;
			break;
		}
	}
});

const checkStandyRule = computed(() => {
	// on the mobile application this condition is used
	// return shift.value.type === shiftType.value.standby ? new Date() : null;
	return shift.value.type === shiftType.value.standby ? true : false;
});
const canSetOnCall = computed(() => {
	let startDate = null;
	const today = dayjs();
	if (shift.value.startDate) {
		startDate = dayjs(shift.value.startDate);
	}
	if (startDate === null) return false;
	if (startDate.isAfter(today, "day")) return false;
	if (startDate.isSame(today, "day")) return true;
	return true;
});

// methods

function setShiftType(type, from) {
	if (
		(type === "sleepover" && shift.value.ward?.hasSleepover) ||
		(type === "standby" && shift.value.ward?.hasOnCall) ||
		type === "regular"
	) {
		// shift.value.type = shiftType.value[type];
		shift.value.type = arrShiftTypesShow.value.find((typ) => typ.id === type);
	} else
		console.log(
			"Didnt change shift type due to ward not having enabled specified shift",
		);
}
function callHandleError(err: any) {
	const errors = parseErrors(err);
	if (Array.isArray(errors) && errors.length > 0) {
		errors.map((e) => {
			toast.openToastError(e);
		});
	} else if (errors) {
		toast.openToastError(errors);
	}
}

async function init() {
	isLoadingShiftData.value = true;
	try {
		await fetchLocations();
		await checkAndFetchShift();
	} catch (error) {
		console.warn(error);
		callHandleError(error);
	} finally {
		isLoadingShiftData.value = false;
	}
}

async function fetchLocations() {
	try {
		const res = await api.getLocationAndCategory();
		formatLocation(res.data.data);
	} catch {}
}

const wardOptions = computed(() => {
	// if (isShiftCreatedViaSelfSubmission.value) {
	// 	return wards.value;
	// } else {
	// Allow only wards that support the selected grade (subcategory)
	const gradeCatId = shift.value.grade?.id;
	return wards.value.filter((ward) =>
		ward.subcategories?.some((sub) => sub.cat_id === gradeCatId),
	);
	// }
});
async function checkAndFetchShift() {
	const id = Number(props.shift_id);
	if (id) {
		const res = await api.showShift(id);
		const r = res.data.data;
		console.log(r);

		shift.value.type = r.type;
		initialShiftType.value = r.type;
		shift.value.startDate = dayjs(r.startTime.date).format("YYYY-MM-DD HH:mm");
		shift.value.endDate = dayjs(r.endTime.date).format("YYYY-MM-DD HH:mm");
		initialStartDate.value = dayjs(r.startTime.date).format("YYYY-MM-DD");
		initialEndDate.value = dayjs(r.endTime.date).format("YYYY-MM-DD");
		shift.value.location = r.location.client;
		shift.value.status = r.status;
		shift.value.breakMinutes = r.breakMinutes;

		if (r.location.client.locations.length) shift.value.ward = r.location; // r.location.client.locations[0];
		if (r.shiftRequest?.subcategories.length)
			shift.value.grade = {
				label: r.shiftRequest.subcategories[0].name,
				...r.shiftRequest.subcategories[0],
			};

		if (r.shiftRequest?.specialShiftName)
			clientNote.value = r.shiftRequest.specialShiftName;

		startTimeSetter(dayjs(r.startTime.date).format("HH:mm:ss"));
		endTimeSetter(dayjs(r.endTime.date).format("HH:mm:ss"));
		// shiftParts
		if (r.shiftParts && r.shiftParts.length > 0) {
			shift.value.additionalTime.isUsed = true;
			console.log("dateeeee", r.shiftParts[0].from);
			shift.value.additionalTime.startDate = dayjs(
				r.shiftParts[0].from?.date,
			).format("YYYY-MM-DD HH:mm");
			shift.value.additionalTime.endDate = dayjs(
				r.shiftParts[0].to?.date,
			).format("YYYY-MM-DD HH:mm");
		}
		refreshTime.value = false;
		nextTick(() => {
			refreshTime.value = true;
		});

		autoCopy();

		if (
			shift.value.ward?.id &&
			shift.value.grade?.id &&
			r.type === shiftType.value.standby
		)
			setShiftType("standby", "after autocopy");
		if (
			shift.value.ward?.id &&
			shift.value.grade?.id &&
			r.type === shiftType.value.sleepover
		)
			setShiftType("sleepover", "after autocopy");

		if (
			shift.value.ward?.id &&
			shift.value.grade?.id &&
			r.type === shiftType.value.regular
		)
			setShiftType("regular", "after autocopy");
		if (
			shift.value.ward?.id &&
			shift.value.grade?.id &&
			r.type === shiftType.value.sleepoverAwaken
		)
			setShiftType("sleepoverAwaken", "after autocopy");
	}
}

function generatFromVal(i) {
	return {
		target: {
			value: i,
		},
	};
}

function autoCopy() {
	for (let i = 0; i < locations.value.length; i++) {
		const loc = locations.value[i];
		if (loc.id == shift.value.location?.id) {
			shift.value.location = loc;

			inputLocation(loc.id);
			break;
		}
	}
	if (shift.value.location && shift.value.location.locations?.length)
		for (let i = 0; i < shift.value.location.locations.length; i++) {
			const ward = shift.value.location.locations[i];
			if (ward.id == shift.value.ward?.id) {
				shift.value.ward = ward;
				// const val = generatFromVal(i);
				inputWard(ward, true);
				break;
			}
		}

	if (
		shift.value.ward &&
		shift.value.ward.subcategories &&
		shift.value.ward.subcategories.length
	)
		for (let i = 0; i < shift.value.ward.subcategories.length; i++) {
			const grade = shift.value.ward.subcategories[i];
			if (grade.id == shift.value.grade?.id) {
				shift.value.grade = { ...grade, label: grade.name };
				// const val = generatFromVal(i);
				inputGrade(grade, true);
				break;
			}
		}
}

function formatLocation(data) {
	locations.value = data;
	if (locations.value.length === 1) {
		const val = generatFromVal(0);
		inputLocation(val);
	}
}
function formatWard() {
	wards.value = shift.value.location?.locations; // locations arr which references on wards
	grades.value = [];
	if (wards.value?.length === 1) {
		const val = generatFromVal(0);
		inputWard(val);
	}
}
function formatGrade() {
	for (const obj of shift.value.ward?.subcategories) {
		obj.label = obj.name;
	}
	grades.value = shift.value.ward?.subcategories; // subcategories arr which references on grades
	if (grades.value.length === 1) {
		const val = generatFromVal(0);
		inputGrade(val);
	}
}

function inputLocation(e) {
	shift.value.location = locations.value.find((loc) => loc.id === e);
	// shift.value.ward = null;
	// shift.value.grade = null;
	// canAddSpecialNote.value = false;
	formatWard();
	removeAllErrors();
}

function inputWard(e, manualInput = false) {
	manualInput
		? (shift.value.ward = e)
		: (shift.value.ward =
				shift.value.location.locations[Number(e.target.value)]);
	// shift.value.grade = null;
	// canAddSpecialNote.value = false;
	formatGrade();

	if (manualInput) {
		// shift.value.grade = null;
		setShiftType("regular", "from wardchange");
	}
	error.value.ward = false;
}

async function inputGrade(e, manualInput = false) {
	manualInput
		? (shift.value.grade = e)
		: (shift.value.grade =
				shift.value.ward.subcategories[Number(e.target.value)]);
	error.value.grade = false;

	let res: any = null;
	try {
		const result = await api.get_temp_calendar_config(
			shift.value.grade?.cat_id,
		);
		res = result.data.data;
	} catch (error) {
		console.warn(error);
		res = null;
	}

	res && res?.shift_start_not_before_date
		? (weekEarlier.value = new Date(res.shift_start_not_before_date))
		: (weekEarlier.value = new Date(
				new Date().setDate(new Date().getDate() - 8),
			));
	// weekEarlier.value.setDate(weekEarlier.value.getDate() - 1);

	res && res?.can_add_special_note
		? (canAddSpecialNote.value = true)
		: (canAddSpecialNote.value = false);
}
watch(
	() => shift.value.grade,
	(newVal) => {
		if (newVal) error.value.grade = false;
	},
);

function startTimeSetter(val) {
	if (val === "") {
		error.value.startTime = true;
		return;
	}
	error.value.startTime = false;
	let final = val;
	if (val.length == 5) final += ":00";
	shift.value.startTime = final;
}

function endTimeSetter(val) {
	if (val === "") {
		error.value.endTime = true;
		return;
	}
	error.value.endTime = false;
	let final = val;
	if (val.length == 5) final += ":00";
	shift.value.endTime = final;
}
function removeAllErrors() {
	const errors = ["location", "ward", "grade", "startDate", "endDate", ,];
	for (const err of errors) {
		removeError(err);
	}
}
function removeError(el) {
	error.value[el] = false;
}
function checkForErrors() {
	let killed = false;
	// let kill = (el) => {
	// 	this.$set(this.error, el, true);
	// 	killed = true;
	// };
	const kill = (el) => {
		error.value[el] = true; // Vue 3's reactivity handles this natively
		killed = true;
	};
	const s = shift.value;
	if (s.location === "" || !s.location) kill("location");
	if (s.ward === "" || !s.ward) kill("ward");
	if (s.grade === "" || !s.grade) kill("grade");
	if (s.startTime === "" || !s.startTime) kill("startTime");
	if (s.endTime === "" || !s.endTime) kill("endTime");
	if (s.startDate === "" || !s.startDate) kill("startDate");
	if (s.endDate === "" || !s.endDate) kill("endDate");

	return killed;
}

async function prepareForSubmitShift() {
	removeAllErrors();

	const err = checkForErrors();
	if (err) {
		toast.openToastError("Please fill all fields.");
		return;
	} else {
		await submitShift();
	}
}

async function submitShift() {
	const shiftL: any = {
		location_id: shift.value.ward?.id ?? undefined,
		// subcategory_id: Number(shift.value.grade.id),
		start_time: shift.value.startDate, // `${formatDateForServer(shift.value.startDate)} ${formatTimeForServer(shift.value.startTime)}`,
		end_time: shift.value.endDate, // `${formatDateForServer(shift.value.endDate)} ${formatTimeForServer(shift.value.endTime)}`,
		shift_type: shift.value.type?.code,
		type: shift.value.type?.code,
		break_minutes: shift.value.breakMinutes,
	};

	// Prevent temps from changing the date of shifts that were created by an admin
	if (!isShiftCreatedViaSelfSubmission.value) {
		if (shiftL.end_time && initialEndDate.value) {
			const selectedDate = dayjs(shiftL.end_time).format("YYYY-MM-DD");

			if (selectedDate !== initialEndDate.value) {
				error.value["endDate"] = true;
				toast.openToastError(
					"You are not authorized to change the date for this shift.",
				);
				return;
			}
		}

		if (shiftL.start_time && initialStartDate.value) {
			const selectedDate = dayjs(shiftL.start_time).format("YYYY-MM-DD");

			if (selectedDate !== initialStartDate.value) {
				error.value["startDate"] = true;
				toast.openToastError(
					"You are not authorized to change the date for this shift.",
				);
				return;
			}
		}
	}
	if (
		(isStandbyShift.value || isSleepoverShift.value) &&
		shift.value.additionalTime.isUsed
	) {
		shiftL.shift_parts = [];
		const shiftPart: any = {
			from: shift.value.additionalTime?.startDate, // `${formatDateForServer(shift.value.additionalTime?.startDate)} ${formatTimeForServer(shift.value.additionalTime?.startTime)}`,
			to: shift.value.additionalTime?.endDate, // `${formatDateForServer(shift.value.additionalTime?.endDate)} ${formatTimeForServer(shift.value.additionalTime?.endTime)}`,
		};
		if (isStandbyShift.value) shiftPart.type = shiftType.value.standby;
		if (isSleepoverShift.value) shiftPart.type = shiftType.value.sleepover;

		shiftL.shift_parts.push(shiftPart);
	}
	if (canAddSpecialNote.value) {
		shiftL.special_shift_name = clientNote.value;
	}
	isLoadingSubmitShift.value = true;
	try {
		await api.submitTempEditShift(props.shift_id, shiftL);
		toast.openToastSuccess("Your shift will be reviewed soon.");
		emit("close-modal");
		emit("confirm");
	} catch (error) {
		callHandleError(error);
	} finally {
		isLoadingSubmitShift.value = false;
	}
}

function displayExplanation() {
	if (canSetOnCall.value || isSleepoverShift.value) return;
	if (shift.value.startDate === null) {
		toast.openToastWarning("Please enter standby date first.");

		return;
	}
	toast.openToastWarning("You cannot set on call times in the future.");
}

function setStartDate(e: any) {
	error.value.startDate = false;
	// shift.value.startDate = e;
	// this.canSetOnCall();
}

onMounted(() => {
	init();
	userStoreOpt.getUserProfile();
});
</script>

<template>
	<div
		class="dialog-container"
		data-cell="edit-user-status"
		@click.stop=""
	>
		<div class="wrapp-inputs">
			<div
				v-if="locations"
				class="flex-column-start"
			>
				<label class="label">
					Location
					<tooltip-edit />
				</label>
				<multiselect-form
					v-if="locations"
					v-model="shift.location"
					:options="locations"
					label="name"
					placeholder="Pick a location"
					value-prop="id"
					:multiple="false"
					:showLabels="true"
					:searchable="true"
					:disabled="true"
				/>
			</div>
			<div class="flex-column-start">
				<label class="label">
					Ward
					<!-- <tooltip-edit /> -->
				</label>
				<multiselect-form
					v-model="shift.ward"
					:options="wardOptions"
					label="name"
					placeholder="Pick a ward"
					value-prop="id"
					:multiple="false"
					:showLabels="true"
					:searchable="true"
				/>
			</div>
			<div
				v-if="
					(tempHasStandby && locationHasStandby) ||
					(tempHasSleepover && locationHasSleepover)
				"
				class="flex-column-start"
			>
				<label class="label">
					Type
					<tooltip-edit />
				</label>
				<multiselect-form
					v-model="shift.type"
					:options="arrShiftTypesShow"
					label="name"
					placeholder="Select shift type"
					value-prop="id"
					:multiple="false"
					:showLabels="true"
					:searchable="true"
					:disabled="true"
				/>
			</div>
			<!-- Grade -->
			<div class="flex-column-start">
				<label class="label">
					Grade
					<tooltip-edit />
				</label>
				<!-- change value-prop if enabled -->
				<multiselect-form
					v-model="shift.grade"
					:options="grades"
					placeholder="Pick a grade"
					value-prop="name"
					:searchable="true"
					:allow-absent="true"
					:disabled="true"
				/>
			</div>

			<!-- Start time-->
			<div class="flex-column-start">
				<label class="label">Start</label>
				<!-- v-model="start_time" -->
				<modern-date-picker
					v-model="shift.startDate"
					placeholder="Start Date/Time"
					:clearable="false"
					:enable-time-picker="true"
					format="dd/MM/yyyy, HH:mm"
					outputFormatString="YYYY-MM-DD HH:mm:ss"
					:min-date="weekEarlier"
					:class="{ error: error.startDate }"
					@update:model-value="setStartDate"
				/>
			</div>

			<!-- End time  -->
			<div class="flex-column-start">
				<label class="label">End</label>
				<!-- v-model="start_time" -->
				<modern-date-picker
					v-model="shift.endDate"
					placeholder="End Date/Time"
					:clearable="false"
					:enable-time-picker="true"
					format="dd/MM/yyyy, HH:mm"
					outputFormatString="YYYY-MM-DD HH:mm:ss"
					:min-date="weekEarlier"
					:class="{ error: error.endDate }"
					@update:model-value="error.endDate = false"
				/>
			</div>
			<div
				v-if="canEditBreakMinutes"
				class="flex-column-start"
			>
				<label class="label">Break</label>
				<div>
					<input
						v-model="shift.breakMinutes"
						class="input"
						type="number"
					/>
					<span style="font-size: smaller; flex: 1 0 auto; margin-left: 1em">
						in minutes
					</span>
				</div>
			</div>
			<!-- checkbox -->
			<div
				v-if="isStandbyShift || isSleepoverShift"
				class="flex-column-start"
			>
				<label
					class="label"
					@click="displayExplanation"
				>
					<input
						v-if="canSetOnCall || isSleepoverShift"
						v-model="shift.additionalTime.isUsed"
						type="checkbox"
						style="margin-left: 10px"
					/>
					<input
						v-else
						:indeterminate="true"
						type="checkbox"
						style="margin-left: 10px"
						disabled
					/>
					{{ additionalTimeTitle }}
				</label>

				<!-- Start time-->

				<div
					v-if="canSetOnCall || isSleepoverShift"
					class="flex-column-start"
				>
					<label class="label">Start</label>
					<!-- v-model="start_time" -->
					<modern-date-picker
						v-model="shift.additionalTime.startDate"
						:defaultDate="new Date().toString()"
						placeholder="Start Date/Time"
						:clearable="false"
						:enable-time-picker="true"
						format="dd/MM/yyyy, HH:mm"
						outputFormatString="YYYY-MM-DD HH:mm:ss"
						:disabled="!shift.additionalTime.isUsed || checkStandyRule"
						:min-date="weekEarlier"
					/>
				</div>

				<!-- End time  -->
				<div
					v-if="canSetOnCall || isSleepoverShift"
					class="flex-column-start"
				>
					<label class="label">End</label>
					<!-- v-model="start_time" -->
					<modern-date-picker
						v-model="shift.additionalTime.endDate"
						placeholder="End Date/Time"
						:clearable="false"
						:enable-time-picker="true"
						format="dd/MM/yyyy, HH:mm"
						outputFormatString="YYYY-MM-DD HH:mm:ss"
						:disabled="!shift.additionalTime.isUsed || checkStandyRule"
						:min-date="weekEarlier"
					/>
				</div>
			</div>
			<div
				v-if="canAddSpecialNote"
				class="note-content-wrap"
			>
				<label class="label">Add note</label>
				<div class="note-input-area">
					<textarea
						v-model="clientNote"
						style="width: 100%; padding-left: 12px"
						placeholder="Type here..."
					></textarea>
				</div>
			</div>
		</div>
		<div class="buttons-centered">
			<button
				class="button is-small alt"
				data-cell="cancel-button"
				@click.prevent="emit('close-modal')"
			>
				Cancel
			</button>
			<button
				class="button"
				:disabled="isLoadingSubmitShift || isLoadingShiftData"
				@click="prepareForSubmitShift"
			>
				Save changes
			</button>
		</div>
	</div>
</template>
<style lang="scss" scoped>
.dialog-container {
	width: 100%;
	box-sizing: border-box;
	padding: 0 20px 20px;
	padding-top: 20px;
	min-width: 564px;
	min-height: 600px;
	max-height: 590px;
	overflow: auto;
	display: flex;
	flex-direction: column;
	justify-content: space-between;
}

.wrapp-inputs {
	// width: 100%;
}
.inputs {
	display: flex;
	justify-content: center;

	.field {
		width: 100%;
	}
}
.error {
	border-color: $error-color !important;
}

.buttons-centered {
	display: flex;
	justify-content: center;
	align-items: center;

	button.button {
		margin: 17px 0.3em;
		min-width: 90px;
		height: 30px;
	}
}
.label {
	color: $label-accent-color;
	text-align: right;
	// width: 40px;
	margin-top: 5px;
	margin-right: 10px;
}
</style>
